import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProjects } from '@/hooks/use-projects';
import { useCreateSiteMonitor, useSiteMonitors } from '@/hooks/use-site-monitors';
import { toast } from '@/hooks/use-toast';
import { Loader2, Globe, Plus } from 'lucide-react';

interface AddSiteMonitorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** If provided, pre-selects this project in the dropdown */
  defaultProjectId?: string;
}

export function AddSiteMonitorDialog({
  open,
  onOpenChange,
  defaultProjectId,
}: AddSiteMonitorDialogProps) {
  const { data: projectsData, isLoading: projectsLoading, isError: projectsError } = useProjects();
  const createMonitor = useCreateSiteMonitor();
  const { data: monitorsData } = useSiteMonitors();

  const projects = projectsData?.data || [];
  const existingMonitorProjectIds = new Set((monitorsData?.data || []).map((m: any) => m.projectId));
  const [selectedProjectId, setSelectedProjectId] = useState(defaultProjectId || '');
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedProjectId(defaultProjectId || '');
      setUrl('');
      setUrlError('');
    }
  }, [open, defaultProjectId]);

  const validateUrl = (value: string): boolean => {
    if (!value.trim()) {
      setUrlError('Website URL is required');
      return false;
    }
    try {
      const parsed = new URL(value.trim());
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        setUrlError('URL must start with http:// or https://');
        return false;
      }
      setUrlError('');
      return true;
    } catch {
      setUrlError('Enter a valid URL (e.g., https://example.com)');
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!selectedProjectId) {
      toast({ title: 'Project required', description: 'Please select a project.', variant: 'destructive' });
      return;
    }
    if (!validateUrl(url)) return;

    try {
      await createMonitor.mutateAsync({
        projectId: selectedProjectId,
        monitoredUrl: url.trim(),
      });
      toast({
        title: 'Monitor added',
        description: 'Site monitoring has been configured. Health checks will run automatically.',
        variant: 'success',
      });
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: 'Failed to add monitor',
        description: err?.message || 'An error occurred.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe size={18} className="text-dyndev-green" />
            Add Site Monitor
          </DialogTitle>
          <DialogDescription>
            Configure health monitoring for a client's website. Checks include uptime, SSL, domain expiry, PageSpeed, security headers, SEO, and CMS detection.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Project selector */}
          <div className="space-y-1.5">
            <Label htmlFor="monitor-project">Project *</Label>
            {projectsLoading ? (
              <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                <Loader2 size={14} className="animate-spin" />
                Loading projects...
              </div>
            ) : projectsError ? (
              <div className="flex flex-col gap-2 py-2">
                <p className="text-sm text-red-400">Failed to load projects.</p>
                <p className="text-xs text-[var(--text-secondary)]">
                  Make sure you have a project created and try again.
                </p>
              </div>
            ) : (
              <Select
                value={selectedProjectId}
                onValueChange={(v) => {
                  setSelectedProjectId(v);
                  setUrlError('');
                }}
              >
                <SelectTrigger id="monitor-project">
                  <SelectValue placeholder="Select a project..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p: any) => {
                    const hasMonitor = existingMonitorProjectIds.has(p.id);
                    return (
                    <SelectItem key={p.id} value={p.id} disabled={hasMonitor && p.id !== defaultProjectId}>
                      {p.name}
                      {p.client?.companyName ? ` — ${p.client.companyName}` : ''}
                      {hasMonitor && p.id !== defaultProjectId ? ' (already monitored)' : ''}
                    </SelectItem>
                  )})}
                  {projects.length === 0 && (
                    <SelectItem value="__none__" disabled>
                      No projects available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* URL input */}
          <div className="space-y-1.5">
            <Label htmlFor="monitor-url">Website URL *</Label>
            <Input
              id="monitor-url"
              type="url"
              value={url}
              onChange={(e) => {
                const val = e.target.value;
                // Auto-prepend https:// if no protocol provided
                if (val && !/^https?:\/\//i.test(val) && val.includes('.')) {
                  setUrl(val.includes('.') && !val.includes('://') ? 'https://' + val : val);
                } else {
                  setUrl(val);
                }
                if (urlError) validateUrl(val);
              }}
              onBlur={() => url.trim() && validateUrl(url)}
              placeholder="https://example.com"
              className={urlError ? 'border-red-500' : ''}
            />
            {urlError && (
              <p className="text-xs text-red-400">{urlError}</p>
            )}
            <p className="text-[10px] text-[var(--text-secondary)]">
              The full URL including https://. SSL, domain, PageSpeed, and SEO checks will run automatically.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMonitor.isPending || !selectedProjectId || !url.trim()}
            className="gap-1.5"
          >
            {createMonitor.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Plus size={14} />
            )}
            {createMonitor.isPending ? 'Adding...' : 'Add Monitor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
